import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useTranslation from "../../virtual_congress/effects/use_translation";
import {useTracking} from "../../virtual_congress/effects/use_tracking";
import Webcam from "react-webcam";
import Countdown from "../components/countdown";
import {PausedVideoStyle} from "../styles";
import {Overlay, OverlayContainer} from "../components/overlay";
import {useVideoFileType, useVideoMimeType} from "../utils";
import {useSelfieUploadState} from "../selfie_context";


const RecordingProgress = ({progress}) => {
  const recordProgressStyle = {
    width: `${progress || 0}%`
  }

  return <>
    <style>
      {`
        .progress-bar {
          -webkit-transition: none;
          -moz-transition: none;
          -ms-transition: none;
          -o-transition: none;
          transition: none;
        }
      `}
    </style>
    <div className="progress rounded-0">
      <div
        aria-valuemax="100"
        aria-valuemin="0"
        aria-valuenow="0"
        className="progress-bar rounded-0"
        role="progressbar"
        style={recordProgressStyle}
      ></div>
    </div>
  </>
}

const LoadingContent = () => {
  const translation = useTranslation()
  return <>
    {translation("selfie_upload.recording_loading", {}, "Loading ...")}
  </>
}

const RecordOverlayContent = ({onClickRecord}) => {
  const translation = useTranslation()

  const style = {
    width: "100%",
    height: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  }

  return <div style={style} onClick={onClickRecord}>
    {translation("selfie_upload.recording_idle", {}, "Tap to start!")}
  </div>
}

const RecordOverlay = ({recordState, onClickRecord}) => {
  if (recordState === RECORD_STATE.LOADING) {
    return <Overlay>
      <LoadingContent/>
    </Overlay>
  } else if (recordState === RECORD_STATE.IDLE) {
    return <Overlay allowPointerEvents={true}>
      <RecordOverlayContent onClickRecord={onClickRecord}/>
    </Overlay>
  }
  return null
}

const RECORD_STATE = {
  LOADING: "LOADING",
  IDLE: "IDLE",
  COUNTDOWN: "COUNTDOWN",
  RECORDING: "RECORDING",
  ERROR: "ERROR",
  FINISHED: "FINISHED",
}

export const CameraRecording = () => {
  const {
    mode,
    setFile,
    targetWidth,
    targetHeight,
    facingMode,
    recordingTime: configRecordingTime
  } = useSelfieUploadState()

  const recordingTime = configRecordingTime || 5

  const track = useTracking()
  const translation = useTranslation()

  const webcamRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [recordProgress, setRecordProgress] = useState(0)
  const [recordState, setRecordState] = useState(RECORD_STATE.LOADING)

  const recordingMimeType = useVideoMimeType()
  const videoFileType = useVideoFileType()

  const startRecording = useCallback(() => {
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: recordingMimeType
    })
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    )
    mediaRecorderRef.current.start()
  }, [webcamRef, mediaRecorderRef]);

  const handleDataAvailable = useCallback(({data}) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => prev.concat(data))
    }
  }, [setRecordedChunks, recordingMimeType])

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current.stop()
  }, [mediaRecorderRef])

  useEffect(() => {
    if (recordState === RECORD_STATE.RECORDING) {
      startRecording()

      let currentTime = 0
      const totalRecordTime = recordingTime * 1000
      const progressionInterval = 10
      let recordInterval = null

      setRecordProgress(0)

      recordInterval = setInterval(() => {
        currentTime += progressionInterval

        const progress = (currentTime / totalRecordTime) * 100
        setRecordProgress(progress)
        if (currentTime >= totalRecordTime) {
          clearInterval(recordInterval)
          stopRecording()
          setRecordState(RECORD_STATE.FINISHED)
        }
      }, progressionInterval)

      return () => {
        clearInterval(recordInterval)
        stopRecording()
      }
    }
  }, [recordingTime, recordState, setRecordState, startRecording, stopRecording])

  useEffect(() => {
    if (recordState === RECORD_STATE.FINISHED && recordedChunks.length) {
      const blob = new Blob(recordedChunks, {
        type: recordingMimeType
      });
      const file = new File([blob], `recording.${videoFileType}`)
      setFile(file)
    }
  }, [
    setFile,
    recordState,
    recordedChunks,
    recordingMimeType,
  ])

  const handleCountdownFinished = useCallback(() => {
    if (mode === "image") {
      const imageSrc = webcamRef.current.getScreenshot();
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], "File name", {type: "image/jpeg"})
          setFile(file)
        })
    } else {
      setRecordState(RECORD_STATE.RECORDING)
    }
  }, [setFile, setRecordState, targetWidth, targetHeight])

  const handleOnUserMedia = useCallback(() => {
    setRecordState(RECORD_STATE.IDLE)
  }, [setRecordState])

  const handleOnUserMediaError = useCallback((error) => {
    console.log(error)
    track("cockpit_recording_error")
    setRecordState(RECORD_STATE.ERROR)
  }, [setRecordState, track])

  const handleOnClickStartRecording = useCallback(() => {
    track("cockpit_recording_record_started")
    setRecordState(RECORD_STATE.COUNTDOWN)
  }, [setRecordState, track])

  const videoStyle = useMemo(() => {
    if (recordState === RECORD_STATE.IDLE) {
      return PausedVideoStyle
    }
    return null
  }, [recordState])

  if (recordState === RECORD_STATE.ERROR) {
    return <OverlayContainer>
      {translation("selfie_upload.recording_error", {}, "Please allow the required permissions.")}
    </OverlayContainer>
  }

  const videoConstraints = {
    width: {ideal: 999999},
    height: {ideal: 999999},
    facingMode: "user"
  }

  //alert("videoConstraints: " + JSON.stringify(videoConstraints))

  return <OverlayContainer>
    <OverlayContainer>
      <RecordOverlay recordState={recordState} onClickRecord={handleOnClickStartRecording}/>
      {recordState === RECORD_STATE.COUNTDOWN && <Countdown finished={handleCountdownFinished}/>}
      <Webcam
        ref={webcamRef}
        audio={true}
        muted={true}
        screenshotFormat={"image/jpeg"}
        videoConstraints={videoConstraints}
        forceScreenshotSourceSize
        onUserMedia={handleOnUserMedia}
        onUserMediaError={handleOnUserMediaError}
        className={"w-100"}
        style={videoStyle}
      />
    </OverlayContainer>
    {mode !== "image" && [RECORD_STATE.RECORDING, RECORD_STATE.COUNTDOWN].includes(recordState) && <>
      <RecordingProgress progress={recordProgress}/>
      {translation("selfie_upload.recording_banner", {}, "Recording")}
    </>}
    {recordState === RECORD_STATE.IDLE && <div className="d-grid gap-2">
      <div
        onClick={handleOnClickStartRecording}
        className="btn btn-primary btn-lg rounded-0">
        {translation("selfie_upload.start_recording_button", {}, "Record")}
      </div>
    </div>}
  </OverlayContainer>
}